import { useState, useMemo } from "react"
import { useTranslation } from "react-i18next"

import TextField from "@/components/form/textField/TextField"
import TextButton from "@/components/button/TextButton"
import Icon from "@/components/icon/Icon"
import FormulaDisplay from "./FormulaDisplay"

import { MetricMeta } from "@/types/metric"
import { extractMetricKeys } from "../utils/formulaTokenizer"
import { evaluateFormula, validateFormula } from "../utils/formulaValidation"
import { useTranslateMetric } from "@/hooks/metric/useTranslateKeyHook"
import { useUnitQuery } from "@/hooks/api/useSettingQuery"
import { convertMetricMeta } from "@/app/report/common/tools"
import { convertUnitSquare } from "@/utils/helper"

interface Props {
  formula: string
  availableMetrics: MetricMeta[]
}

export default function TestFormula({ formula, availableMetrics }: Props) {
  const { t } = useTranslation()
  const translateMetric = useTranslateMetric()
  const { data: selectedUnitList } = useUnitQuery()
  const [expanded, setExpanded] = useState(false)
  const [testValues, setTestValues] = useState<Record<string, number>>({})

  const metricKeys = extractMetricKeys(formula)

  // 메트릭의 단위를 팀 설정에 맞게 변환
  const getDisplayUnit = (metric: MetricMeta) => {
    const convertedUnit = convertMetricMeta(metric, selectedUnitList)
    return convertedUnit ? convertUnitSquare(convertedUnit) : metric.unit
  }

  // 테스트에 필요한 메트릭 정보
  const usedMetrics = metricKeys
    .map((key) => availableMetrics.find((m) => m.key === key))
    .filter((m): m is MetricMeta => m !== undefined)

  // 수식 유효성 검사 (실시간)
  const formulaValidation = useMemo(() => validateFormula(formula), [formula])
  const formulaError = !formulaValidation.isValid
    ? t(formulaValidation.error!, { ...formulaValidation.errorParams })
    : undefined

  // 결과 계산
  const result = useMemo(() => {
    if (!formula.trim() || !formulaValidation.isValid) return null
    // 메트릭이 있으면 모든 값이 입력되어야 계산
    if (metricKeys.length > 0) {
      const hasAllValues = metricKeys.every(
        (key) => testValues[key] !== undefined && testValues[key] !== null,
      )
      if (!hasAllValues) return null
    }
    return evaluateFormula(formula, testValues)
  }, [formula, testValues, metricKeys, formulaValidation.isValid])

  const handleValueChange = (key: string, value: string) => {
    const numValue = parseFloat(value)
    if (value === "" || isNaN(numValue)) {
      const newValues = { ...testValues }
      delete newValues[key]
      setTestValues(newValues)
    } else {
      setTestValues((prev) => ({ ...prev, [key]: numValue }))
    }
  }

  const enabled = metricKeys.length > 0

  return (
    <div className={`TestFormulaCont ${enabled ? "enabled" : ""}`}>
      {/* 토글 버튼 */}
      <div className="toggle-header">
        <TextButton disabled={!enabled} onClick={() => setExpanded(!expanded)}>
          {t("testFormula")}
          <Icon type={expanded ? "arrow-up" : "arrow-down"} size={16} />
        </TextButton>
      </div>

      {/* 펼쳐진 내용 */}
      {enabled && expanded && (
        <div className="test-content">
          {/* 테스트 입력 필드들 */}
          <div className="test-inputs">
            {usedMetrics.map((metric) => (
              <TextField
                key={metric.key}
                className="test-input"
                label={`${translateMetric(metric)} (${getDisplayUnit(metric)})`}
                type="number"
                value={testValues[metric.key] ?? ""}
                onChange={(e) =>
                  handleValueChange(metric.key, (e.target as HTMLInputElement).value)
                }
                size="small"
              />
            ))}
          </div>

          {/* 결과 */}
          <div className="test-result">
            <span className="result-label">{t("result")}</span>
            <div className={`result-box ${formulaError ? "error" : ""}`}>
              <FormulaDisplay
                formula={formula}
                availableMetrics={availableMetrics}
                testValues={testValues}
                showResult={true}
                result={result}
              />
            </div>
            {formulaError && <span className="result-error">{formulaError}</span>}
          </div>
        </div>
      )}
    </div>
  )
}
